import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { browserTz, conferenceLabel, publicApi, type Booking } from '../api';
import { ChangeMeeting } from '../components/ChangeMeeting';
import { ErrorBox, Loading, PublicFrame } from '../components/bk';

/** Invitee reschedule / cancel page reached from the email link (/manage/:id?t=token). */
export function Manage() {
  const { id } = useParams();
  const [q, setQ] = useSearchParams();
  const [token, setToken] = useState(q.get('t') ?? '');
  const [b, setB] = useState<Booking | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const tz = browserTz();
  const action = q.get('a') === 'cancel' ? 'cancel' : q.get('a') === 'reschedule' ? 'reschedule' : undefined;

  useEffect(() => {
    publicApi.get<Booking>(`/api/public/bookings/${id}?t=${encodeURIComponent(token)}`).then(setB).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!b) return <PublicFrame>{error ? <ErrorBox error={error} /> : <Loading />}</PublicFrame>;
  const s = DateTime.fromISO(b.start).setZone(tz);
  const hostNames = (b.hosts ?? []).map((h) => h.name).join(', ');

  return (
    <PublicFrame>
      <section className="card">
        <div className="hostline">{hostNames}</div>
        <h1>{b.linkTitle}</h1>
        {notice && <div className="alert ok">{notice}</div>}
        {b.status === 'canceled' ? (
          <p><span className="badge err">Canceled</span> <s className="muted">{s.toFormat('cccc, LLLL d · h:mm a')}</s></p>
        ) : (
          <>
            <p className="when">{s.toFormat('cccc, LLLL d')} · {s.toFormat('h:mm a')} – {DateTime.fromISO(b.end).setZone(tz).toFormat('h:mm a ZZZZ')}</p>
            <p className="muted">
              {conferenceLabel[b.conference.type]}
              {b.conference.joinUrl && <> · <a href={b.conference.joinUrl}>Join link</a></>}
              {b.conference.dialIn && <> · {b.conference.dialIn}{b.conference.passcode && ` (code ${b.conference.passcode})`}</>}
            </p>
          </>
        )}
      </section>

      {b.status === 'confirmed' && (
        <section className="card">
          <ChangeMeeting
            timezone={tz}
            initialMode={action}
            fetchSlots={(from, to) =>
              publicApi
                .get<{ slots: string[] }>(`/api/public/bookings/${b.id}/slots?t=${encodeURIComponent(token)}&from=${from}&to=${to}`)
                .then((r) => r.slots)
            }
            onReschedule={async (start, reason) => {
              const r = await publicApi.post<{ booking: Booking; manageToken: string }>(`/api/public/bookings/${b.id}/reschedule`, { t: token, start, reason });
              // The token rotates on every change; keep this page usable.
              setToken(r.manageToken);
              setQ({ t: r.manageToken }, { replace: true });
              setB(r.booking);
              setNotice('Rescheduled. An updated invitation is on its way.');
            }}
            onCancel={async (reason) => {
              setB(await publicApi.post<Booking>(`/api/public/bookings/${b.id}/cancel`, { t: token, reason }));
              setNotice('Canceled. The hosts have been notified.');
            }}
          />
        </section>
      )}
    </PublicFrame>
  );
}
